import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Checkbox } from '../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { selectCurrentClient } from '../../../../../CommonSelectors/CommonAreaSelector';
import { Donor, GivingStrategy, WorkflowDTO } from '../../api/useCharitableGivingQuery';
import { INPUTS_WIDTH } from '../../CharitableGiving.meta';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { useNavigation } from '../../hooks/useNavigation';
import { FormValues, STEP_ID, validation } from './DonationYear.meta';

export const DonationYear = () => {
  const { workflow, setWorkflow, setStep } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({ currentStep: STEP_ID });

  const {
    data: { donationStartYear, donationEndYear, givingStrategy, donor, isRecurring },
    options: { partnerQcdYearOptions, principalQcdYearOptions, donationYearOptions },
  } = workflow;

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      donationStartYear,
      donationEndYear,
      isRecurring,
    },
    resolver: yupResolver(validation),
  });

  const isRecurringValue = watch('isRecurring');

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const onSubmit = handleSubmit(values => {
    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        ...values,
        donationStartYear: values.donationStartYear,
        donationEndYear: isRecurringValue ? values.donationEndYear : null,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const hasDonor = donor !== null;

  const rawQcdYearOptions =
    hasDonor && donor === Donor.Spouse ? partnerQcdYearOptions : principalQcdYearOptions;

  const rawYearOptions =
    givingStrategy === GivingStrategy.QualifiedCharitableDonations
      ? rawQcdYearOptions
      : donationYearOptions;

  const yearOptions = rawYearOptions.map(year => ({
    label: year.toString(),
    value: year,
    rightLabel:
      primaryYob && hasDonor
        ? donor === Donor.Principal
          ? `Age ${year - primaryYob}`
          : spouseYob
          ? `Age ${year - spouseYob}`
          : ''
        : primaryYob
        ? `Age ${year - primaryYob}${spouseYob ? `, Age ${year - spouseYob}` : ''}`
        : undefined,
  }));

  return (
    <StepContainer
      title="When would you like to make the donation?"
      onSubmit={onSubmit}
      footer={
        <StepFooter
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
        />
      }
    >
      <Box display="flex" flexDirection="column" style={{ gap: 32 }}>
        <Box display="flex" style={{ gap: 16 }}>
          <Controller
            control={control}
            name="donationStartYear"
            render={({ field: { value, onChange, ref } }) => (
              <Box style={{ width: INPUTS_WIDTH }}>
                <Select
                  innerRef={ref}
                  label={isRecurring ? 'Start Year' : 'Donation Year'}
                  value={yearOptions.find(opt => opt.value === value)}
                  options={yearOptions}
                  onChange={opt => onChange(opt?.value)}
                  formVariant={errors.donationStartYear ? 'error' : undefined}
                  helperText={errors.donationStartYear?.message}
                />
              </Box>
            )}
          />

          {isRecurringValue && (
            <Controller
              control={control}
              name="donationEndYear"
              render={({ field: { value, onChange, ref } }) => (
                <Box style={{ width: INPUTS_WIDTH }}>
                  <Select
                    innerRef={ref}
                    label="End Year"
                    value={yearOptions.find(opt => opt.value === value)}
                    options={yearOptions}
                    onChange={opt => onChange(opt?.value)}
                    formVariant={errors.donationEndYear ? 'error' : undefined}
                    helperText={errors.donationEndYear?.message}
                  />
                </Box>
              )}
            />
          )}
        </Box>

        <Checkbox {...register('isRecurring')}>Recurring</Checkbox>
      </Box>
    </StepContainer>
  );
};
